import { useQuery } from "@apollo/client";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { getResolvedNotReportingReasonSentence } from "encamp-shared/src/tierii/oakNotReportingReason";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { prettyPrintDate } from "util/dates";

import { SubmissionRecords } from "./Submitted/SubmissionRecords";
import { useReport } from "./useReport";

const FACILITY_NOT_REPORTING_STATUS = gql(`
  query FacilityNotReportingStatus($id: ID!) {
    facility(id: $id) {
      status
      colocationReportingFacility {
        id
        name
        customerFacilityId
      }
      isNotReporting
      isNotReportingThroughEncamp
      dateClosed
      dateInactive
    }
  }
`);

export const NotReporting = () => {
  const theme = useTheme();
  const { data: report } = useReport();

  const { data } = useQuery(FACILITY_NOT_REPORTING_STATUS, {
    variables: { id: report?.tierIIReport.facility.id ?? "" },
    skip: !report,
    fetchPolicy: "cache-and-network",
  });

  // Prefer the stored reason (the only source for EXEMPT); fall back to facility-derived.
  const facility = data?.facility;
  const message =
    getResolvedNotReportingReasonSentence({
      storedReason: report?.tierIIReport.notReportingReason,
      storedNote: report?.tierIIReport.notReportingReasonNote,
      facility: facility
        ? {
            status: facility.status,
            colocationReportingFacilityId: facility.colocationReportingFacility?.id ?? null,
            colocationReportingFacility: facility.colocationReportingFacility
              ? {
                  name: facility.colocationReportingFacility.name,
                  customerFacilityId: facility.colocationReportingFacility.customerFacilityId,
                }
              : null,
            isNotReporting: facility.isNotReporting,
            isNotReportingThroughEncamp: facility.isNotReportingThroughEncamp,
            dateClosed: facility.dateClosed,
            dateInactive: facility.dateInactive,
          }
        : undefined,
      reportingYear: report?.tierIIReport.reportingYear,
    }) ?? "";

  return (
    <Stack gap={theme.spacing(6)}>
      <Box>
        <Typography variant="h5">Facility Not Reporting</Typography>
        <Typography variant="caption">
          No {prettyPrintEnumValue(report?.tierIIReport.reportKind)} report will be filed for this
          facility.
        </Typography>
      </Box>

      <Box>
        <Typography variant="body1" sx={{ mb: theme.spacing(1) }}>
          <i>Reason for not reporting:</i>
        </Typography>
        <Typography variant="body1">{message}</Typography>
        {data?.facility?.dateInactive && (
          <Typography>Date inactive: {prettyPrintDate(data.facility.dateInactive)}</Typography>
        )}
        {data?.facility?.dateClosed && (
          <Typography>Date closed: {prettyPrintDate(data.facility.dateClosed)}</Typography>
        )}
      </Box>
      {report?.tierIIReport && <SubmissionRecords report={report?.tierIIReport} />}
    </Stack>
  );
};
